import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Sheet, Stack,
    Tab, TabList, TabPanel, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { useNavigate, useParams } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsCampaignApprove, APIAdminAdsCampaignDetail,
    APIAdminAdsCampaignForceArchive, APIAdminAdsCampaignForcePause,
    APIAdminAdsCampaignReject, APIAdminAdsCampaignRequestChanges,
    APIAdminAdsCreativeApprove, APIAdminAdsCreativeReject, adminAdsError,
} from '../../../api_admin_ads';
import AdModerationCreativeRow from '../../../components/Ads/AdModerationCreativeRow';
import AdRejectModal from '../../../components/Ads/AdRejectModal';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import {
    OBJECTIVE_LABELS, PRICING_LABELS, STRATEGY_LABELS, formatMoney,
} from '../../../components/Ads/adsConstants';


function Row({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
            <Typography level="body-sm" color="neutral">{label}</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, textAlign: 'right' }}>
                {value ?? '—'}
            </Typography>
        </Stack>
    );
}


export default function AdminAdsCampaignPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { adminToken: token, has } = useAdmin();

    const canModerate = has('ads.moderate');
    const canManage = has('ads.manage');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState(0);
    const [rejectTarget, setRejectTarget] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsCampaignDetail(token, campaignId);
            setData(res);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить кампанию'));
        } finally {
            setLoading(false);
        }
    }, [token, campaignId]);

    useEffect(() => { load(); }, [load]);

    const run = async (key, fn, successText) => {
        setBusy(key);
        setError(null);
        setNotice(null);
        try {
            await fn();
            if (successText) setNotice(successText);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    const submitReject = async ({ reason, comment }) => {
        const target = rejectTarget;
        setBusy('reject');
        try {
            if (target.kind === 'campaign') {
                if (target.mode === 'changes') {
                    await APIAdminAdsCampaignRequestChanges(token, campaignId, { reason, comment });
                    setNotice('Кампания возвращена на доработку');
                } else {
                    await APIAdminAdsCampaignReject(token, campaignId, { reason, comment });
                    setNotice('Кампания отклонена');
                }
            } else {
                await APIAdminAdsCreativeReject(token, target.id, { reason, comment });
            }
            setRejectTarget(null);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
    }

    if (!data?.campaign) {
        return (
            <Box>
                <Alert color="danger" sx={{ mb: 2 }}>{error || 'Кампания не найдена'}</Alert>
                <Button variant="outlined" color="neutral"
                        startDecorator={<ArrowBackRoundedIcon />}
                        onClick={() => navigate('/admin/ads/campaigns')}>
                    К списку
                </Button>
            </Box>
        );
    }

    const campaign = data.campaign;
    const advertiser = data.advertiser || {};
    const logs = data.moderation_log || [];
    const creatives = (campaign.groups || []).flatMap((g) => g.creatives || []);

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
                   justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}
                   sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Button size="sm" variant="plain" color="neutral"
                            startDecorator={<ArrowBackRoundedIcon />}
                            onClick={() => navigate('/admin/ads/campaigns')}>
                        К списку
                    </Button>
                    <Divider orientation="vertical" />
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography level="h4">{campaign.name}</Typography>
                            <AdStatusChip
                                status={campaign.status}
                                systemPaused={campaign.system_paused}
                                systemPauseReason={campaign.system_pause_reason}
                            />
                        </Stack>
                        <Typography level="body-xs" color="neutral">
                            ID {campaign.id} · аккаунт #{advertiser.account_id}
                            {advertiser.partner_id ? ` · партнёр #${advertiser.partner_id}` : ''}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {canModerate && campaign.status === 'moderation' && (
                        <>
                            <Button color="success" startDecorator={<CheckRoundedIcon />}
                                    loading={busy === 'approve'}
                                    onClick={() => run('approve',
                                        () => APIAdminAdsCampaignApprove(token, campaignId),
                                        'Кампания одобрена')}>
                                Одобрить
                            </Button>
                            <Button color="warning" variant="soft"
                                    onClick={() => setRejectTarget({
                                        kind: 'campaign', mode: 'changes',
                                        subject: `Кампания «${campaign.name}»`,
                                    })}>
                                На доработку
                            </Button>
                            <Button color="danger" variant="soft" startDecorator={<BlockRoundedIcon />}
                                    onClick={() => setRejectTarget({
                                        kind: 'campaign', mode: 'reject',
                                        subject: `Кампания «${campaign.name}»`,
                                    })}>
                                Отклонить
                            </Button>
                        </>
                    )}
                    {canManage && ['active', 'scheduled', 'out_of_budget'].includes(campaign.status) && (
                        <Button variant="soft" color="neutral" startDecorator={<PauseRoundedIcon />}
                                loading={busy === 'force-pause'}
                                onClick={() => run('force-pause',
                                    () => APIAdminAdsCampaignForcePause(
                                        token, campaignId, 'Остановлено администратором'),
                                    'Кампания принудительно остановлена')}>
                            Остановить
                        </Button>
                    )}
                    {canManage && campaign.status !== 'archived' && (
                        <Button variant="plain" color="neutral" startDecorator={<Inventory2RoundedIcon />}
                                loading={busy === 'force-archive'}
                                onClick={() => run('force-archive',
                                    () => APIAdminAdsCampaignForceArchive(token, campaignId),
                                    'Кампания архивирована')}>
                            В архив
                        </Button>
                    )}
                </Stack>
            </Stack>

            {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Объявления ({creatives.length})</Tab>
                    <Tab>Настройки</Tab>
                    <Tab>История модерации ({logs.length})</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0 }}>
                    <Stack spacing={1.5}>
                        {creatives.map((creative) => (
                            <AdModerationCreativeRow
                                key={creative.id}
                                creative={creative}
                                canModerate={canModerate}
                                busy={busy}
                                onApprove={(c) => run(`approve-${c.id}`,
                                    () => APIAdminAdsCreativeApprove(token, c.id))}
                                onReject={(c) => setRejectTarget({
                                    kind: 'creative', id: c.id,
                                    subject: `Объявление «${c.title}»`,
                                })}
                            />
                        ))}
                        {creatives.length === 0 && (
                            <Alert color="warning">В кампании нет объявлений</Alert>
                        )}
                    </Stack>
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', flex: 1 }}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>Рекламодатель</Typography>
                            <Row label="Аккаунт" value={advertiser.title} />
                            <Row label="Юр. наименование" value={advertiser.legal_name} />
                            <Row label="ИНН" value={advertiser.legal_inn} />
                            <Row label="Баланс" value={`${formatMoney(advertiser.balance)} ₽`} />
                            <Row
                                label="Автомодерация"
                                value={advertiser.is_trusted ? 'включена' : 'выключена'}
                            />
                        </Sheet>

                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', flex: 1 }}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>Кампания</Typography>
                            <Row label="Цель" value={OBJECTIVE_LABELS[campaign.objective]} />
                            <Row label="Модель оплаты" value={PRICING_LABELS[campaign.pricing_model]} />
                            <Row label="Стратегия" value={STRATEGY_LABELS[campaign.bid_strategy]} />
                            <Row
                                label="Ставка"
                                value={campaign.pricing_model === 'cpc'
                                    ? `${formatMoney(campaign.bid_cpc)} ₽/клик`
                                    : `${formatMoney(campaign.bid_cpm)} ₽/1000`}
                            />
                            <Row label="Дневной бюджет" value={`${formatMoney(campaign.daily_budget)} ₽`} />
                            <Row label="Потрачено" value={`${formatMoney(campaign.spent_total)} ₽`} />
                        </Sheet>
                    </Stack>

                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mt: 2 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>Площадки</Typography>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap">
                            {(campaign.campaign_placements || []).map((link) => (
                                <Chip key={link.id} variant="soft" color="primary">
                                    {link.placement_name}
                                </Chip>
                            ))}
                        </Stack>
                    </Sheet>
                </TabPanel>

                <TabPanel value={2} sx={{ p: 0 }}>
                    <Stack spacing={1}>
                        {logs.map((log) => (
                            <Sheet key={log.id} variant="soft" sx={{ p: 1.5, borderRadius: 'md' }}>
                                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" spacing={1}>
                                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                                        <Chip size="sm" variant="outlined">{log.status_from || '—'}</Chip>
                                        <Typography level="body-xs">→</Typography>
                                        <Chip size="sm" variant="soft"
                                              color={log.status_to === 'rejected' ? 'danger' : 'success'}>
                                            {log.status_to}
                                        </Chip>
                                        {log.creative && (
                                            <Chip size="sm" variant="outlined">объявление #{log.creative}</Chip>
                                        )}
                                    </Stack>
                                    <Typography level="body-xs" color="neutral">
                                        {log.date_create
                                            ? format(new Date(log.date_create), 'd MMM yyyy, HH:mm', { locale: ru })
                                            : ''} · {log.admin_name}
                                    </Typography>
                                </Stack>
                                {(log.reason_display || log.comment) && (
                                    <Typography level="body-sm" sx={{ mt: 0.6 }}>
                                        {log.reason_display}
                                        {log.comment ? ` — ${log.comment}` : ''}
                                    </Typography>
                                )}
                            </Sheet>
                        ))}
                        {logs.length === 0 && (
                            <Typography level="body-sm" color="neutral">История пуста</Typography>
                        )}
                    </Stack>
                </TabPanel>
            </Tabs>

            <AdRejectModal
                open={!!rejectTarget}
                mode={rejectTarget?.mode}
                subject={rejectTarget?.subject}
                saving={busy === 'reject'}
                onClose={() => setRejectTarget(null)}
                onSubmit={submitReject}
            />
        </Box>
    );
}